// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/NowForYou.tsx
// 🔧 PURPOSE: "Oggi · mercoledì 23 settembre · Adesso per te" delle viste
//             S e A: al massimo TRE card, scelte dal momento del soggiorno,
//             in quest'ordine di importanza (plan §4).
//
//             Soggiorno (dal giorno dell'arrivo alle 15:00):
//             1. il check-in online se manca e si può ancora fare (la
//                finestra chiude il giorno dopo l'arrivo): pesca, urgente;
//             2. la tassa di soggiorno chiesta e non pagata: pesca, l'importo
//                e PAGA (si paga subito: il codice lo attiva lo staff dopo);
//             3. SOLO il giorno dell'arrivo, "Come arrivare": dalle 15:00 la
//                Home è già quella del soggiorno (tessere codice, WiFi,
//                reception), ma chi è ancora in viaggio deve ritrovare le
//                indicazioni (Google Maps, come la tessera della vista B).
//                Senza indirizzo né coordinate la card non c'è;
//             4. la pulizia a metà soggiorno, se il catalogo ha ancora giorni
//                (extra_cleaning con cleaningDates): SCEGLI IL GIORNO. Non
//                c'è un orario da prenotare: "dalle 7 alle 11" è solo testo;
//             5. l'evento vicino (oggi, domani, dopodomani): "STASERA ·
//                21:00" solo se l'ora è nota, e i km da Mola; il tocco apre
//                la pagina Eventi con lui in cima (lib/eventFocus);
//             6. senza pulizia né evento, "La tua casa" (clima e lavatrice),
//                solo se la casa si comanda (check-in fatto, nel soggiorno).
//             Vigilia della partenza: tassa, spazzatura ("domani si parte"),
//             il taxi se non ce n'è già uno (altrimenti La tua casa),
//             l'evento. Giorno della partenza: tassa, spazzatura prima di
//             uscire, taxi; dopo l'uscita (stayOver) niente spazzatura né
//             taxi sotto casa: l'ospite è già partito. L'orario di uscita
//             sta già nella card del soggiorno ("Check-out oggi entro le
//             10:00").
//
//             Dopo la partenza (A): la recensione (/my-stay?open=review) e
//             "Torna a trovarci" (/book). Niente che comandi la casa.
//
//             Check-in anticipato (anche nella vista B, via HomeBefore): il
//             giorno prima dell'arrivo e quello dell'arrivo, "Arrivi prima?
//             Entra già dalle 12:00 · da 20 €" con "Scegli l'orario", finché
//             c'è una fascia acquistabile adesso, non è già comprato e
//             l'ospite non ha dichiarato di arrivare dopo l'orario di
//             check-in (lib/earlyCheckin, earlyCheckinOffer). Al posto della
//             notifica del mattino, per decisione del proprietario.
//
//             La spazzatura apre la pagina dei rifiuti (lo
//             stesso pannello di MyStay: fuori dal soggiorno il server
//             risponde "non disponibile").
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarPlus,
  CarTaxiFront,
  ChevronRight,
  Clock,
  Home as HomeIcon,
  MapPin,
  Sparkles,
  Star,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import type { ExtrasCatalog, GuestBooking } from '../../lib/guestApi';
import { useEvents, type Event } from '../../contexts/EventsContext';
import { describeEventWhen, eventPlace, formatEventWhen, nearEvent } from '../../lib/homeEvents';
import { eventFocusPath } from '../../lib/eventFocus';
import { EXTRA_GROUPS, extraDetailPath, extraGroupPath } from '../../lib/extraGroups';
import { EventThumb } from './EventsInDates';
import { hasDirections, openDirections } from '../../lib/directions';
import { useServiceDialogs } from './services/ServiceDialogsHost';
import { formatDayKey } from './format';
import TouristTaxDue from './TouristTaxDue';
import CheckInRedoNote from '../CheckInRedoNote';
import { EARLY_CHECKIN_ID, earlyCheckinOffer } from '../../lib/earlyCheckin';
import { formatEuro } from '../TouristTaxWidget';
import { Callout, CardShell, DueBlock, IconCircle, InkButton, INK_TEXT, MUTED_TEXT, Section, SectionTitle } from './ui';

/** Mai più di tre card: "adesso" vuol dire poche cose. */
export const MAX_NOW_CARDS = 3;

/** Da dove si arriva: il "torna indietro" degli extra riporta alla Home. */
const FROM_HOME = { from: '/' };

const CLEANING_ID = 'extra_cleaning';
const TAXI_GROUP = EXTRA_GROUPS.filter((g) => g.id === 'taxi')[0];

export type NowCardKey =
  | 'checkin'
  | 'tax'
  | 'earlyCheckin'
  | 'directions'
  | 'cleaning'
  | 'event'
  | 'home'
  | 'trashEve'
  | 'trashCheckout'
  | 'taxi'
  | 'review'
  | 'bookAgain';

/** Quello che non sta nello stato della Home: catalogo ed eventi. */
export interface NowCardInputs {
  /** La pulizia a metà soggiorno ha ancora giorni in vendita. */
  cleaning: boolean;
  /** C'è un evento oggi, domani o dopodomani. */
  event: boolean;
  /** Un taxi per la partenza si può ancora comprare (o il catalogo non è ancora arrivato). */
  taxiOnSale: boolean;
  /** La prenotazione ha l'indirizzo o le coordinate dell'appartamento ("Come arrivare"). */
  directions?: boolean;
  /** Il check-in anticipato si propone adesso (earlyCheckinOffer). */
  earlyCheckin?: boolean;
}

type NowState = Pick<
  HomeState,
  | 'view'
  | 'checkInDone'
  | 'checkInWindowOpen'
  | 'taxDue'
  | 'isArrivalDay'
  | 'isEve'
  | 'isCheckoutDay'
  | 'stayOver'
  | 'homeAvailable'
  | 'transferBooked'
  | 'houseCheckInTime'
>;

/** Le card da mostrare, già in ordine e già tagliate a tre. */
export function nowCardKeys(state: NowState, inputs: NowCardInputs): NowCardKey[] {
  if (state.view === 'after') return ['review', 'bookAgain'];
  // Vista B (prima dell'arrivo, e il giorno dell'arrivo fino al check-in):
  // "Adesso per te" c'è solo per il check-in anticipato. Check-in e tassa
  // stanno già in "Da fare".
  if (state.view === 'before') return inputs.earlyCheckin ? ['earlyCheckin'] : [];
  if (state.view !== 'stay') return [];

  const list: NowCardKey[] = [];
  // Dal giorno dopo l'arrivo il channel manager non lo accetta più: la card
  // porterebbe a una pagina che dice "fuori tempo".
  if (!state.checkInDone && state.checkInWindowOpen) list.push('checkin');
  if (state.taxDue) list.push('tax');
  // Il giorno dell'arrivo dopo l'ora di check-in le fasce sono di solito
  // passate; se il gestore ne ha una più tarda, si propone ancora.
  if (inputs.earlyCheckin) list.push('earlyCheckin');
  // Il giorno dell'arrivo (nella vista S: dalle 15:00) le indicazioni per
  // arrivare, subito dopo le cose da fare: nella card del soggiorno ci sono
  // già codice, WiFi e reception, e non c'è più la tessera della vista B.
  if (state.isArrivalDay && inputs.directions) list.push('directions');

  const taxi = !state.transferBooked && inputs.taxiOnSale;
  if (state.isCheckoutDay) {
    // Dopo l'uscita niente che si faccia in casa o sotto casa.
    if (!state.stayOver) {
      list.push('trashCheckout');
      if (taxi) list.push('taxi');
    }
  } else if (state.isEve) {
    list.push('trashEve');
    if (taxi) list.push('taxi');
    else if (state.homeAvailable) list.push('home');
    if (inputs.event) list.push('event');
  } else {
    if (inputs.cleaning) list.push('cleaning');
    if (inputs.event) list.push('event');
    if (!inputs.cleaning && !inputs.event && state.homeAvailable) list.push('home');
  }
  return list.slice(0, MAX_NOW_CARDS);
}

/** La pulizia a metà soggiorno si compra ancora: il servizio è in vendita e ha dei giorni. */
export function cleaningOnSale(catalog: ExtrasCatalog | null): boolean {
  if (!catalog || !catalog.success) return false;
  return (catalog.items || []).some(
    (i) => i.serviceId === CLEANING_ID && i.available && (i.options.cleaningDates || []).length > 0,
  );
}

/**
 * Un taxi per andare via: un servizio del gruppo taxi in vendita, e per i
 * transfer con il listino la tratta di ritorno ancora aperta. Senza
 * catalogo (non ancora arrivato) si propone come faceva la vecchia Home:
 * la pagina del gruppo dice lei cosa c'è.
 */
export function departureTaxiOnSale(catalog: ExtrasCatalog | null): boolean {
  if (!catalog || !catalog.success) return true;
  return (catalog.items || []).some(
    (i) =>
      TAXI_GROUP.serviceIds.indexOf(i.serviceId) >= 0 &&
      i.available &&
      (!i.options.transfer || i.options.transfer.departureOpen !== false),
  );
}

// ---- Le card -----------------------------------------------------------------------

interface NowLinkProps {
  /** Dove porta; senza `to` è un bottone con `onClick` (le indicazioni si aprono fuori dall'app). */
  to?: string;
  onClick?: () => void;
  state?: unknown;
  /** L'icona nel cerchio espresso; oppure `media` (la foto dell'evento). */
  icon?: LucideIcon;
  media?: React.ReactNode;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
}

/** Una card che porta altrove: icona (o foto), occhiello, titolo, testo, freccia. */
const NowLink: React.FC<NowLinkProps> = ({ to, onClick, state, icon: Icon, media, eyebrow, title, desc }) => {
  const className = cn(
    'flex min-h-[72px] w-full items-center gap-3 rounded-[18px] p-2.5 text-left transition-colors hover:bg-nva-bone/70 dark:hover:bg-stone-800/60',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400',
    !media && 'px-3.5',
  );
  const body = (
    <>
      {media || (Icon ? <IconCircle tone="ink"><Icon /></IconCircle> : null)}
      <span className="flex min-w-0 flex-grow flex-col gap-0.5">
        {eyebrow ? (
          <span className="text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.14em] text-nva-gold-text dark:text-nva-gold-dark">
            {eyebrow}
          </span>
        ) : null}
        <span className={cn('line-clamp-2 break-words text-[15px] font-bold leading-snug', INK_TEXT)}>{title}</span>
        {desc ? <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{desc}</span> : null}
      </span>
      <ChevronRight className="h-[18px] w-[18px] flex-shrink-0 text-nva-muted dark:text-stone-500" aria-hidden="true" />
    </>
  );
  return (
    <CardShell className="overflow-hidden">
      {to ? (
        <Link to={to} state={state} className={className}>{body}</Link>
      ) : (
        <button type="button" onClick={onClick} className={className}>{body}</button>
      )}
    </CardShell>
  );
};

/** L'evento vicino: la foto (o l'iniziale del paese), quando, cosa, dove. Porta a lui, in cima agli Eventi. */
const EventCard: React.FC<{ event: Event; now: Date }> = ({ event, now }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  return (
    <NowLink
      to={eventFocusPath(event)}
      media={<EventThumb event={event} />}
      eyebrow={formatEventWhen(describeEventWhen(event, now), t, lang)}
      title={event.title}
      desc={<span className="block truncate">{eventPlace(event, t)}</span>}
    />
  );
};

// ---- La sezione ----------------------------------------------------------------------

export interface NowForYouProps {
  state: HomeState;
  /** L'ora del clock della Home: "Stasera", "Domani" e l'evento vicino cambiano con lei. */
  now: Date;
  booking: GuestBooking;
  catalog: ExtrasCatalog | null;
  className?: string;
}

const NowForYou: React.FC<NowForYouProps> = ({ state, now, booking, catalog, className }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const { getAllEvents } = useEvents();
  const { openService } = useServiceDialogs();
  const after = state.view === 'after';
  const titleId = 'home-now-title';

  // Gli eventi servono solo nel soggiorno, e non il giorno della partenza.
  const wantEvents = state.view === 'stay' && !state.isCheckoutDay;
  const [events, setEvents] = useState<Event[] | null>(null);
  useEffect(() => {
    if (!wantEvents) {
      setEvents(null);
      return undefined;
    }
    let alive = true;
    getAllEvents()
      .then((list) => { if (alive) setEvents(list); })
      .catch(() => { if (alive) setEvents([]); });
    return () => { alive = false; };
  }, [wantEvents, getAllEvents]);
  const event = useMemo(() => (events ? nearEvent(events, now) : null), [events, now]);

  const early = earlyCheckinOffer({ catalog, booking, now, houseCheckInTime: state.houseCheckInTime });
  const keys = nowCardKeys(state, {
    cleaning: cleaningOnSale(catalog),
    event: !!event,
    taxiOnSale: departureTaxiOnSale(catalog),
    directions: hasDirections(booking),
    earlyCheckin: !!early,
  });
  if (keys.length === 0) return null;

  const trashAction = (
    <InkButton onClick={() => openService('recycle')}>{t('home.now.trashCta')}</InkButton>
  );

  const cards = keys.map((key) => {
    switch (key) {
      case 'checkin':
        // Rifiutato dal gestore: va rifatto, col motivo e la nota.
        return state.checkInRedo ? (
          <DueBlock
            key={key}
            badge={t('checkInRedo.badge')}
            title={t('checkInRedo.title')}
            desc={t('checkInRedo.desc')}
            action={<InkButton to={state.checkInPath}>{t('checkInRedo.cta')}</InkButton>}
            note={<CheckInRedoNote redo={state.checkInRedo} className="mt-0.5" />}
          />
        ) : (
          <DueBlock
            key={key}
            badge={t('home.before.urgent')}
            title={t('home.before.checkinTitle')}
            desc={t('home.now.checkinUrgent')}
            action={<InkButton to={state.checkInPath}>{t('home.before.checkinCta')}</InkButton>}
          />
        );
      case 'tax':
        return <TouristTaxDue key={key} booking={booking} variant="stay" />;
      case 'earlyCheckin':
        return early ? (
          <Callout
            key={key}
            icon={<Clock className="text-nva-gold-text dark:text-nva-gold-dark" />}
            title={t('home.now.earlyTitle', { time: early.fromTime })}
            action={<InkButton to={extraDetailPath(EARLY_CHECKIN_ID)} state={FROM_HOME}>{t('home.now.earlyCta')}</InkButton>}
          >
            {t('home.now.earlyDesc', { price: formatEuro(early.price, lang) })}
          </Callout>
        ) : null;
      case 'directions':
        return (
          <NowLink
            key={key}
            onClick={() => openDirections(booking.apartmentAddress, booking.apartmentLatitude, booking.apartmentLongitude)}
            icon={MapPin}
            eyebrow={t('home.now.arrivalEyebrow')}
            title={t('home.quick.directions')}
            desc={<span className="line-clamp-2 break-words">{booking.apartmentAddress || t('home.now.directionsDesc')}</span>}
          />
        );
      case 'cleaning':
        return (
          <Callout
            key={key}
            icon={<Sparkles className="text-nva-gold-text dark:text-nva-gold-dark" />}
            title={t('home.now.cleaningTitle')}
            action={<InkButton to={extraDetailPath(CLEANING_ID)} state={FROM_HOME}>{t('home.now.cleaningCta')}</InkButton>}
          >
            {t('home.now.cleaningDesc')}
          </Callout>
        );
      case 'event':
        return event ? <EventCard key={key} event={event} now={now} /> : null;
      case 'home':
        return (
          <NowLink
            key={key}
            to="/my-stay?open=climate"
            icon={HomeIcon}
            title={t('home.services.homeTitle')}
            desc={t('home.smart.cards.homeControls.desc')}
          />
        );
      case 'trashEve':
      case 'trashCheckout':
        return (
          <Callout
            key={key}
            icon={<Trash2 className="text-nva-gold-text dark:text-nva-gold-dark" />}
            title={t(`home.smart.cards.${key}.title`)}
            action={trashAction}
          >
            {t(`home.smart.cards.${key}.desc`)}
          </Callout>
        );
      case 'taxi':
        return (
          <NowLink
            key={key}
            to={extraGroupPath('taxi')}
            state={FROM_HOME}
            icon={CarTaxiFront}
            title={t('home.smart.cards.transfer.title')}
            desc={t('home.smart.cards.transfer.desc')}
          />
        );
      case 'review':
        return (
          <NowLink
            key={key}
            to="/my-stay?open=review"
            icon={Star}
            title={t('home.smart.cards.review.title')}
            desc={t('home.smart.cards.review.desc')}
          />
        );
      case 'bookAgain':
      default:
        return (
          <NowLink
            key={key}
            to="/book"
            icon={CalendarPlus}
            title={t('home.smart.cards.bookAgain.title')}
            desc={t('home.smart.cards.bookAgain.desc')}
          />
        );
    }
  });

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle
        id={titleId}
        eyebrow={after
          ? t('home.after.eyebrow')
          : t('home.now.eyebrow', { date: formatDayKey(state.today, lang, { weekday: 'long', day: 'numeric', month: 'long' }) })}
        title={after ? t('home.after.title') : t('home.now.title')}
      />
      {cards}
    </Section>
  );
};

export default NowForYou;
